<template>
  <v-form 
    ref="form" 
    v-model="valid"                 
    lazy-validation>
    <v-dialog 
      v-if="!placement.locked"
      v-model="dialog" 
      persistent 
      scrollable
      max-width="1200px">
      <v-fab-transition 
        v-if="placement['.key']" 
        slot="activator">
        <v-btn 
          slot="activator" 
          fab
          dark
          small
          absolute
          bottom
          right
          color="secondary">
          <v-icon dark>edit</v-icon>
        </v-btn>
      </v-fab-transition>
      <v-fab-transition 
        v-else 
        slot="activator">
        <v-btn
          slot="activator" 
          color="secondary"
          fab
          dark
          absolute
          bottom            
          right
        >
          <v-icon>add</v-icon>
        </v-btn>
      </v-fab-transition>
    
    
      <v-card light>
        <v-toolbar 
          color="primary" 
          dark>
          <v-toolbar-title id="starter">
            <span class="headline">Create placement</span>
          </v-toolbar-title>
        </v-toolbar>
        <v-divider/>
        <v-card-text>
          <v-container grid-list-md>
            <v-layout 
              v-if="!placement['.key']"
              row 
              wrap>
              <v-flex 
                xs12 
                sm6 
                lg4 
                offset-lg2>
                <v-select
                  :items="courses"
                  :rules="[v => !!v || 'Course is required']"
                  v-model="selectedCourse"
                  label="Course"
                  required
                  return-object
                  @input="loadUnits"                          
                >
                  <template 
                    slot="selection" 
                    slot-scope="data">
                    {{ data.item.code }}
                  </template>
                  <template 
                    slot="item" 
                    slot-scope="data">
                    <v-list-tile-content>
                      <v-list-tile-title v-html="data.item.code"/>
                      <v-list-tile-sub-title v-html="data.item.name"/>
                    </v-list-tile-content>
                  </template>
                </v-select>
              </v-flex>
              <v-flex 
                xs12 
                sm6 
                lg4>
                <v-select
                  v-if="units.length > 0"
                  :rules="[v => !!v || 'Unit is required']"                          
                  :items="units"
                  v-model="selectedUnit"
                  label="Units"
                  required
                  return-object
                  @input="loadAgreements"                          
                >
                  <template 
                    slot="selection" 
                    slot-scope="data">
                    {{ data.item.code }}
                  </template>
                  <template 
                    slot="item" 
                    slot-scope="data">
                    <v-list-tile-content>
                      <v-list-tile-title v-html="data.item.code"/>
                      <v-list-tile-sub-title v-html="data.item.name"/>
                    </v-list-tile-content>
                  </template>
                </v-select>
                <span v-else>Select course to get a list of units.</span>
              </v-flex>
            </v-layout>
            <v-layout 
              v-if="!placement['.key']"
              row 
              wrap>
              <v-flex 
                xs12 
                sm6 
                lg4 
                offset-lg2>
                <v-select
                  v-model="placement.campus"
                  :rules="[v => !!v || 'Campus is required']"
                  :items="['Burwood', 'Waterfront', 'Warrnambool', 'Waurn Ponds', 'Cloud']"
                  label="Campus"                          
                  required                          
                />
              </v-flex>
              <v-flex 
                xs12 
                sm6 
                lg4>
                <v-select
                  :items="organisations"
                  :rules="[v => !!v || 'Organisation is required']"
                  v-model="selectedOrganisation"
                  label="Organisation / Venue"
                  required
                  return-object                         
                >
                  <template 
                    slot="selection" 
                    slot-scope="data">
                    {{ data.item.fullname }}
                  </template>
                  <template 
                    slot="item" 
                    slot-scope="data">
                    <v-list-tile-content>
                      <v-list-tile-title v-html="data.item.fullname"/>
                    </v-list-tile-content>
                  </template>
                </v-select>
              </v-flex>
            </v-layout>
            <v-layout 
              v-else 
              row 
              wrap>
              <v-flex 
                xs12 
                sm8 
                offset-lg2>
                <v-alert 
                  :value="true" 
                  type="info">
                  Contact placement staff if you need to remove or update this placement.
                </v-alert>
              </v-flex>
            </v-layout>
            <v-layout 
              row 
              wrap>
              <v-flex 
                xs12 
                sm6 
                lg4 
                offset-lg2>
                <v-text-field
                  v-model="placement.studentId"
                  :rules="[
                    v => !!v || 'ID is required',
                    v => !v || v.length <= 14 || 'ID must be under 15 characters',
                    v => !v || v.length >= 5 || 'ID must be at least 5 characters',
                    v => /^\d{5,14}$/.test(v) || 'ID expects numbers only'
                  ]"
                  label="Student ID"
                  required
                />
              </v-flex>             
            </v-layout>
            <v-layout 
              row 
              wrap>
              <v-flex 
                xs12 
                md6 
                lg4 
                offset-lg2>
                <v-menu
                  ref="menuStartDate"
                  :close-on-content-click="false"
                  v-model="menuStartDate"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    :value="computedStartDate"
                    :rules="[v => !!v || 'Start date is required']"
                    label="Start date"
                    prepend-icon="event"
                    readonly
                  />
                  <v-date-picker 
                    v-model="placement.startDate"
                    color="green lighten-1"
                    no-title
                    @input="$refs.menuStartDate.save(placement.startDate)"/>
                </v-menu>
              </v-flex>
              <v-flex 
                xs12 
                md6 
                lg4>
                <v-menu
                  ref="menuEndDate"
                  :close-on-content-click="false"
                  v-model="menuEndDate"
                  :nudge-right="40"
                  lazy
                  transition="scale-transition"
                  offset-y
                  full-width
                  max-width="290px"
                  min-width="290px"
                >
                  <v-text-field
                    slot="activator"
                    :value="computedEndDate"
                    :rules="[v => !!v || 'End date is required']"
                    label="End date"
                    prepend-icon="event"
                    readonly
                  />
                  <v-date-picker 
                    v-model="placement.endDate"
                    color="cyan darken-1"
                    no-title
                    @input="$refs.menuEndDate.save(placement.endDate)"/>
                </v-menu>
              </v-flex>
            </v-layout>
            <v-flex 
              xs12 
              lg8 
              offset-lg2>
              <v-layout 
                v-for="agreement in agreements" 
                :key="agreement.itemOrder" 
                align-center                         
                class="mt-3">
                <v-checkbox
                  :rules="[v => !!v || 'You must agree to continue!']"
                  v-model="agreement.value"
                  hide-details
                  class="shrink mr-2"
                  required
                  color="success"
                >
                  <label 
                    slot="label" 
                    v-html="agreement.html"/>
                </v-checkbox>
              </v-layout>
            </v-flex>
          </v-container>          
        </v-card-text>
        <v-progress-linear 
          v-if="loading"
          :indeterminate="loading"/>
        <v-divider/>        
        <v-card-actions>
          <v-spacer/>
          <v-btn 
            color="primary" 
            flat 
            @click.native="close">Close</v-btn>
          <v-btn 
            v-if="placement['.key']"
            color="primary" 
            flat 
            @click.native="update(placement)">Update</v-btn>
          <v-btn 
            v-else
            color="primary" 
            flat 
            @click.native="save(placement)">Save</v-btn>
        </v-card-actions>
      
      </v-card>
    
    </v-dialog>
  </v-form>
</template>

<script>
export default {
  name: "EditForm",
  props: {
    edititem: {
      type: [Array, Object],
      default: null
    }
  },
  data() {
    return {
      dialog: false,
      confirm: false,
      menuStartDate: false,
      menuEndDate: false,
      valid: true,
      nameRules: [
        v => !!v || "Name is required",
        v => (v && v.length < 100) || "Name must be less than 100 characters"
      ],
      codeRules: [
        v => !!v || "Code is required",
        v => (v && v.length !== 5) || "Code must be 6 or 7 characters",
        v =>
          /^([A-Z]{3}[0-9]{3}[a-z]?)+$/.test(v) ||
          'Expecting 3 capitalised letters followed by 3 numbers. ("HNN300" / "HNN300b")'
      ],
      selectedCourse: null,
      selectedUnit: null,
      selectedOrganisation: null,
      organisations: [],
      courses: [],
      units: [],
      agreements: [],
      placement: JSON.parse(JSON.stringify(this.edititem)),
      nextStep: null,
      nextStepType: null
    };
  },
  computed: {
    computedStartDate() {
      return this.formatDate(this.placement.startDate);
    },
    computedEndDate() {
      return this.formatDate(this.placement.endDate);
    },
    user() {
      return this.$store.getters.user;
    },
    loading() {
      return this.$store.getters.loading;
    }
  },
  firestore() {
    return {
      courses: this.$store.getters.db.collection("courses").orderBy("code"),
      organisations: this.$store.getters.db
        .collection("roles/organisation/users")
        .orderBy("fullname")
    };
  },
  watch: {
    organisations: function(val) {
      let self = this;
      if (this.placement[".key"]) {
        val.forEach(function(o) {
          if (o.username === self.placement.organisation) {
            self.selectedOrganisation = o;
          }
        });
      }
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${day}/${month}/${year}`;
    },
    save(placement) {
      if (this.$refs.form.validate()) {
        console.log("saving", placement);
        this.dialog = false;
        let self = this;
        this.$store.dispatch("setLoading", true);
        placement.uid = this.user.uid;
        placement.displayName = this.user.displayName;
        placement.email = this.user.email;
        placement.course = this.selectedCourse.code;
        placement.courseName = this.selectedCourse.name;
        placement.unit = this.selectedUnit.code;
        placement.unitName = this.selectedUnit.name;
        placement.instructions = this.selectedUnit.instructions
          ? this.selectedUnit.instructions
          : "";
        placement.organisation = this.selectedOrganisation.username;
        placement.organisationName = this.selectedOrganisation.fullname;
        placement.organisationEmail = this.selectedOrganisation.email;
        placement.nextStep = this.nextStep;
        placement.nextStepType = this.nextStepType;
        this.$store.getters.db
          .collection("students")
          .doc(this.user.uid)
          .collection("placements")
          .add({ ...placement })
          .then(ref => {
            self.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "cyan darken-2",
              text: placement.unit + " placement added"
            });
            let alert = {
              text:
                "Creating " +
                placement.unit +
                " placement. It may take up to a minute for the backend to finish creating your placement.",
              type: "info",
              show: true
            };
            self.$emit("alertText", alert);
            self.$refs.form.reset();
            self.$store.dispatch("setLoading", false);
          })
          .catch(err => {
            self.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "error",
              text: "Error occured creating placement"
            });
            let alert = {
              text:
                "An error occured creating placement. Please ask a staff member to review your placement details.",
              type: "error",
              show: true
            };
            self.$emit("alertText", alert);
            self.$refs.form.reset();
            self.$store.dispatch("setLoading", false);
            console.log(err);
          });
      }
    },
    update(placement) {
      if (this.$refs.form.validate()) {
        let self = this;
        this.dialog = false;
        let updatePlacement = JSON.parse(JSON.stringify(placement));
        this.$store.dispatch("setLoading", true);
        delete updatePlacement[".key"];
        delete updatePlacement.createdAt;
        updatePlacement.lastUpdate = new Date();
        // updatePlacement.organisation = this.selectedOrganisation.username
        // updatePlacement.organisationName = this.selectedOrganisation.fullname
        this.$store.getters.db
          .collection("students")
          .doc(this.user.uid)
          .collection("placements")
          .doc(placement[".key"])
          .update(updatePlacement)
          .then(ref => {
            self.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "cyan darken-2",
              text: placement.unit + " placement updated"
            });
            // self.$refs.form.reset()
            self.$store.dispatch("setLoading", false);
          })
          .catch(err => {
            self.$store.dispatch("sendNotification", {
              snackbar: true,
              color: "error",
              text: "Error occured creating placement"
            });
            // self.$refs.form.reset()
            self.$store.dispatch("setLoading", false);
            console.log(err);
          });
      }
    },
    close() {
      this.$store.dispatch("sendNotification", {
        snackbar: true,
        color: "red darken-2",
        text: "Closed (not saved)"
      });
      // this.$refs.form.reset()
      this.dialog = false;
    },
    loadUnits() {
      if (this.selectedCourse) {
        let self = this;
        this.$store.dispatch("setLoading", true);
        this.$binding(
          "units",
          this.$store.getters.db
            .collection("courses")
            .doc(this.selectedCourse[".key"])
            .collection("units")
        ).then(units => {
          self.$store.dispatch("setLoading", false);
        });
      } else {
        this.units = [];
      }
    },
    loadAgreements() {
      if (this.selectedUnit) {
        console.log("loading agreements");
        let self = this;
        this.$store.dispatch("setLoading", true);
        this.$binding(
          "agreements",
          this.$store.getters.db
            .collection("courses")
            .doc(this.selectedCourse[".key"])
            .collection("units")
            .doc(this.selectedUnit[".key"])
            .collection("agreements")
            .orderBy("itemOrder")
        ).then(querySnapshot => {
          console.log(querySnapshot);
          self.$store.dispatch("setLoading", false);
        });
        // Also load nextStep
        this.$store.getters.db
          .collection("courses")
          .doc(this.selectedCourse[".key"])
          .collection("units")
          .doc(this.selectedUnit[".key"])
          .collection("steps")
          .where("itemOrder", "==", 1)
          .get()
          .then(querySnapshot => {
            querySnapshot.forEach(doc => {
              this.nextStep = doc.data().name;
              this.nextStepType = doc.data().type;
            });
          });
      } else {
        this.units = [];
      }
    }
  }
};
</script>
